import { createContext, useContext, ReactNode, useMemo, useState, useCallback, useEffect } from "react";
import type { InfiniteData } from "@tanstack/react-query";
import { useSwivuCompanyCam } from "@/react/hooks/entityHooks/useSwivuCompanyCam";
import { EntityRecord } from "@/types/entity";
import { EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { getPresignedImageUrlForFile } from "@/network/network";
import { downloadFileFromData, showToast } from "@/other/utils/utils";
import { CompanyCamStatusBanner } from "@/integrations";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuFile, type SwivuFileListPage } from "@/react/components/entityViews/files/types/swivuFile";
import { queryClient } from "@/lib/queryClient";
import { useEntityFiles, useFileUpload, useFileDelete, FileEntityType } from "../api/files.api";
import { fileKeys, FileListFilters } from "../api/files.query-keys";
import { useCompanyCamImportState } from "../hooks/useCompanyCamImportState";
import type { FileViewerMode } from "@/react/components/entityViews/files/components/editors/FileDetailModal/types";

type CompanyCamProjectOps = ReturnType<typeof useSwivuCompanyCam>;

export type OpenFileOptions = {
  mode?: FileViewerMode;
};

type EntityFileContextValue = CompanyCamProjectOps & {
  entityRecord: EntityRecord;
  // Gallery state
  files: SwivuFile[];
  totalCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoadingFiles: boolean;
  isErrorFiles: boolean;
  refetch: () => void;
  // Upload
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  isUploading: boolean;
  // Delete
  deleteFile: (fileId: number) => Promise<void>;
  isDeleting: boolean;
  // File editor UI state
  isFileEditorOpen: boolean;
  setIsFileEditorOpen: (open: boolean) => void;
  openedFile: SwivuFile | null;
  handleFileOpen: (file: SwivuFile, options?: OpenFileOptions) => void;
  openedFileMode: FileViewerMode;
  clearOpenedFileMode: () => void;
  handleFileDownload: (file: SwivuFile) => void;
  /** True briefly after a fresh CompanyCam project link while imports may arrive. */
  isImportingCompanyCam: boolean;
};

const EntityFileContext = createContext<EntityFileContextValue | undefined>(undefined);

interface EntityFileProviderProps {
  children: ReactNode;
  record: EntityRecord;
  entityType: EntityTypeKey;
  /** Optional filter applied to the file list query (e.g. { fileType: "image" }). */
  filters?: FileListFilters;
}

/* Used inside the TaskFilesEditor therefore taking the record and entityType as props */
export const EntityFileProvider: React.FC<EntityFileProviderProps> = ({
  children,
  record,
  entityType,
  filters = {},
}) => {
  const { selectedBoard } = useSwivuBryntumContext();
  const boardId = selectedBoard?.id;

  const [isFileEditorOpen, setIsFileEditorOpen] = useState(false);
  const [openedFileId, setOpenedFileId] = useState<number | null>(null);
  const [openedFileSnapshot, setOpenedFileSnapshot] = useState<SwivuFile | null>(null);
  const [openedFileMode, setOpenedFileMode] = useState<FileViewerMode>("default");

  const VALID_FILE_ENTITY_TYPES = new Set<string>(["task", "resource", "client", "location"]);
  if (!VALID_FILE_ENTITY_TYPES.has(entityType)) {
    console.error(`EntityFileProvider: unsupported entityType "${entityType}" — no file queries will run`);
  }
  const fileEntityType = entityType as FileEntityType;
  const entityId = Number(record.id);

  const companyCamOperations = useSwivuCompanyCam({ record });

  const {
    isImportingCompanyCam,
    fileListStaleTime,
    fileListRefetchInterval,
    reportTotalCount,
  } = useCompanyCamImportState({
    entityType: record.entityType,
    entityId,
  });

  const entityFilesResult = useEntityFiles({
    boardId: boardId ?? 0,
    entityType: fileEntityType,
    entityId,
    filters,
    enabled: !!boardId,
    staleTime: fileListStaleTime,
    refetchInterval: fileListRefetchInterval,
  });

  useEffect(() => {
    reportTotalCount(entityFilesResult.totalCount);
  }, [entityFilesResult.totalCount, reportTotalCount]);

  // Mutations receive boardId??0 but are only callable after the panel renders,
  // by which point selectedBoard is always defined. The guard below prevents
  // accidental mutations targeting boardId:0 if somehow called before mount.
  const { uploadFiles, optimisticFiles, isUploading } = useFileUpload({
    boardId: boardId ?? 0,
    entityType: fileEntityType,
    entityId,
  });

  const { deleteFile, isDeleting } = useFileDelete({
    boardId: boardId ?? 0,
    entityType: fileEntityType,
    entityId,
  });

  // Optimistic files appear at the top of the gallery
  const files = useMemo(
    () => [...optimisticFiles, ...entityFilesResult.files],
    [optimisticFiles, entityFilesResult.files]
  );

  // Prefer the live filtered list, then any sibling entity-file cache, then the
  // open-time snapshot so tab filter changes don't unmount FileDetailModal.
  const openedFile = useMemo(() => {
    if (openedFileId == null) return null;
    const fromList = files.find((f) => f.id === openedFileId);
    if (fromList) return fromList;

    if (boardId) {
      const cachedQueries = queryClient.getQueriesData<InfiniteData<SwivuFileListPage>>({
        queryKey: fileKeys.entity(boardId, fileEntityType, entityId),
      });
      for (const [, data] of cachedQueries) {
        const found = data?.pages.flatMap((page) => page.results).find((f) => f.id === openedFileId);
        if (found) return found;
      }
    }

    return openedFileSnapshot?.id === openedFileId ? openedFileSnapshot : null;
  }, [files, openedFileId, openedFileSnapshot, boardId, fileEntityType, entityId]);

  const handleFileOpen = useCallback((file: SwivuFile, options?: OpenFileOptions) => {
    if (file.isOptimistic) return;
    setOpenedFileId(file.id);
    setOpenedFileSnapshot(file);
    setOpenedFileMode(options?.mode ?? "default");
    setIsFileEditorOpen(true);
  }, []);

  const clearOpenedFileMode = useCallback(() => {
    setOpenedFileMode("default");
  }, []);

  const handleFileDownload = useCallback(async (file: SwivuFile) => {
    try {
      const response = await getPresignedImageUrlForFile(file.id);
      const url: string = response.data.url;
      if (!url) return;

      const fetchResponse = await fetch(url);
      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }
      const blob = await fetchResponse.blob();
      downloadFileFromData(blob, file.name || "download");
    } catch (error) {
      console.error("error downloading file", error);
      showToast({ msg: "Failed to download file. Please try again.", type: "error", duration: 3000 });
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      ...companyCamOperations,
      entityRecord: record,
      // Gallery
      files,
      totalCount: entityFilesResult.totalCount,
      fetchNextPage: entityFilesResult.fetchNextPage,
      hasNextPage: entityFilesResult.hasNextPage,
      isFetchingNextPage: entityFilesResult.isFetchingNextPage,
      isLoadingFiles: entityFilesResult.isLoading,
      isErrorFiles: entityFilesResult.isError,
      refetch: entityFilesResult.refetch,
      // Upload
      uploadFiles,
      isUploading,
      // Delete
      deleteFile,
      isDeleting,
      // File editor UI
      isFileEditorOpen,
      setIsFileEditorOpen,
      openedFile,
      handleFileOpen,
      openedFileMode,
      clearOpenedFileMode,
      handleFileDownload,
      isImportingCompanyCam,
    }),
    [
      companyCamOperations,
      record,
      files,
      entityFilesResult.totalCount,
      entityFilesResult.fetchNextPage,
      entityFilesResult.hasNextPage,
      entityFilesResult.isFetchingNextPage,
      entityFilesResult.isLoading,
      entityFilesResult.isError,
      entityFilesResult.refetch,
      uploadFiles,
      isUploading,
      deleteFile,
      isDeleting,
      isFileEditorOpen,
      openedFile,
      handleFileOpen,
      openedFileMode,
      clearOpenedFileMode,
      handleFileDownload,
      isImportingCompanyCam,
    ]
  );

  return (
    <EntityFileContext.Provider value={contextValue}>
      <CompanyCamStatusBanner
        error={companyCamOperations.companyCamError}
        onDismiss={companyCamOperations.clearCompanyCamError}
        visible={companyCamOperations.hasCompanyCamApiKey && Boolean(record.companyCamProject)}
      />
      {children}
    </EntityFileContext.Provider>
  );
};

export const useEntityFileContext = (): EntityFileContextValue => {
  const context = useContext(EntityFileContext);
  if (!context) {
    throw new Error("useEntityFileContext must be used within EntityFileProvider");
  }
  return context;
};
